import { useState, useEffect } from 'react';
import { useAppDispatch } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import * as api from 'api/methods';

import {
  getLessonCategories,
  removeLessonCategoryById,
} from 'store/lesson-categories/actions';

import { useModal } from 'hooks/useModal';

const DEFAULT_ITEMS_PER_PAGE = 50;

export const useLessonCategories = ({
  categoriesPerPage,
}: {
  categoriesPerPage: any;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [
    removeConfirmOpen,
    openRemoveConfirm,
    closeRemoveConfirm,
    removeConfirmData,
  ] = useModal({
    defaultOpen: false,
  });

  const [loading, setLoading] = useState(true);
  const [sortParams, setSortParams] = useState({
    sortBy: 'createdAt',
    sortDirection: 'desc',
  });

  useEffect(() => {
    const _getItems = async () => {
      try {
        await dispatch(
          getLessonCategories({
            page: 1,
            limit: DEFAULT_ITEMS_PER_PAGE,
            query: '',
            sortBy: sortParams.sortBy,
            sortDirection: sortParams.sortDirection,
          }),
        ).unwrap();
      } catch (error) {
        console.warn(error);
      }

      setLoading(false);
    };

    _getItems();
  }, []);

  const handleCategoryRowClick = (categoryId: string) => {
    navigate(`/lesson-categories/${categoryId}`);
  };

  const handleCategoryEditClick = (category: any) => {
    navigate(`/lesson-categories/${category.id}`);
  };

  const handleCategoryRemoveClick = (item: any) => {
    openRemoveConfirm({
      title: 'Do you really want to remove lesson category?',
      text: '',
      data: {
        item,
      },
    });
  };

  const handleConfirmRemoveClick = ({ item }: { item: any }) => {
    closeRemoveConfirm();

    dispatch(removeLessonCategoryById(item.id));
  };

  const handleCategoryCreateClick = async () => {
    try {
      const defaultData = {
        name: `Category ${uuid()}`,
      };

      const category = await api.createLessonCategory(defaultData);

      navigate(`/lesson-categories/${category.id}`);
    } catch (error) {
      console.warn(error); // TODO: catch error
    }
  };

  const handleSortChange = async (nextSortBy: any) => {
    try {
      await dispatch(
        getLessonCategories({
          page: 1,
          limit: categoriesPerPage,
          sortBy: nextSortBy,
          sortDirection: sortParams.sortDirection === 'asc' ? 'desc' : 'asc',
        }),
      ).unwrap();

      setSortParams((prevState: any) => ({
        sortBy: nextSortBy,
        sortDirection: prevState.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } catch (error) {
      console.warn(error);
    }
  };

  return {
    loading,
    removeConfirmOpen,
    sortParams,
    removeConfirmData,
    closeRemoveConfirm,
    onSortChange: handleSortChange,
    onCategoryRowClick: handleCategoryRowClick,
    onCategoryEditClick: handleCategoryEditClick,
    onCategoryRemoveClick: handleCategoryRemoveClick,
    onConfirmRemoveClick: handleConfirmRemoveClick,
    onCategoryCreateClick: handleCategoryCreateClick,
  };
};
